import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Button } from 'reactstrap';
import { PropertiesList } from '../../models/propertyModel';
import { getLandlordByMailEndpoint } from '../../../../services/landlordService';

import useAuth from '../../../../hooks/useAuth';
import useCallApiAndLoad from '../../../../hooks/useCallApiAndLoad';

interface Landlord {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  area_code?: string;
  phone?: string;
  [key: string]: any;
}

interface Props {
  isOpen: boolean;
  toggle: () => void;
  ownerProperty: PropertiesList | null;
  setOwnerProperty: (property: PropertiesList | null) => void;
  onSave: (editableInfo: any) => Promise<void>;
  areaCodes: any[];
}

const EditOwnerModal: React.FC<Props> = ({
  isOpen,
  toggle,
  ownerProperty,
  setOwnerProperty,
  onSave,
  areaCodes,
}) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Landlord[]>([]);
  const [loading, setLoading] = useState(false);

  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const callApiAndLoad = useCallApiAndLoad();

  const [landlordInfo, setLandlordInfo] = useState<Partial<Landlord>>(ownerProperty?.landlordInfo ?? {});
  useEffect(() => {
    setLandlordInfo(ownerProperty?.landlordInfo ?? {});
  }, [ownerProperty?.landlordInfo]);

  const hasNoOwner = !ownerProperty?.landlord_id;

  const [editableInfo, setEditableInfo] = useState({
    name: landlordInfo.name ?? '',
    email: landlordInfo.email ?? '',
    phone: landlordInfo.phone ?? '',
    area_code: landlordInfo.area_code ?? '',
    first_name: landlordInfo.first_name ?? '',
    last_name: landlordInfo.last_name ?? '',
  });
  useEffect(() => {
    setEditableInfo({
      name: landlordInfo.name ?? '',
      email: landlordInfo.email ?? '',
      phone: landlordInfo.phone ?? '',
      area_code: landlordInfo.area_code ?? '',
      first_name: landlordInfo.first_name ?? '',
      last_name: landlordInfo.last_name ?? '',
    });
  }, [landlordInfo]);

  if (!ownerProperty) return null;

  const handleSearch = async () => {
    setLoading(true);
    try {
      const { status, data } = await callApiAndLoad.callEndpoint(
        getLandlordByMailEndpoint(search.trim().toLowerCase(), token)
      );
      if (data && typeof data === 'object' && data.data.landlord_id) {
        ownerProperty.landlord_id = data.data.landlord_id;
        setResults([data.data]);
      } else {
        setResults([]);
      }
    } catch (e) {
      setResults([]);
    }
    setLoading(false);
  };

  const handleSelect = (landlord: any) => {
    const newInfo = {
      landlord_id: landlord.landlord_id,
      name: landlord.name,
      email: landlord.email,
      phone: landlord.phone ?? '',
      avatar: landlord.avatar ?? '',
      first_name: landlord.first_name ?? '',
      last_name: landlord.last_name ?? '',
      area_code: landlord.area_code ?? '',
    };
    setOwnerProperty({
      ...ownerProperty,
      landlordInfo: newInfo,
      landlord_id: landlord.landlord_id,
    });
    setLandlordInfo(newInfo);
    setResults([]);
    setSearch('');
  };

  const canEditOwnerFields = true;

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="edit-modal">
      <ModalHeader toggle={toggle} className="edit-modal-header">
        Editar propietario
      </ModalHeader>
      <ModalBody className="edit-modal-body">
        <FormGroup>
          <Label for="landlord_search">Buscar propietario</Label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              id="landlord_search"
              type="text"
              placeholder="Nombre, apellido o correo"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
            <Button color="primary" onClick={handleSearch} disabled={loading}>
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
          
          {results.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {results.map(l => (
                <div
                  key={l.landlord_id}
                  style={{ cursor: 'pointer', padding: 4, borderBottom: '1px solid #eee', background: '#f8f9fa' }}
                  onClick={() => handleSelect(l)}
                >
                  {l.name} - {l.email}
                </div>
              ))}
            </div>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="owner_name">Nombre</Label>
          <Input
            id="owner_name"
            type="text"
            value={editableInfo.name}
            disabled={hasNoOwner ? false : true}
            onChange={e => setEditableInfo({ ...editableInfo, name: e.target.value })}
            className="edit-modal-input"
          />
        </FormGroup>
        <FormGroup>
          <Label for="owner_email">Correo</Label>
          <Input
            id="owner_email"
            type="email"
            value={editableInfo.email}
            disabled={hasNoOwner ? false : true}
            onChange={e => setEditableInfo({ ...editableInfo, email: e.target.value })}
            className="edit-modal-input"
          />
        </FormGroup>
        <FormGroup>
          <Label for="owner_phone">Teléfono</Label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              type="select"
              id="owner_area_code"
              value={editableInfo.area_code}
              disabled={false} 
              onChange={e => setEditableInfo({ ...editableInfo, area_code: e.target.value })}
              className="edit-modal-input"
              style={{ width: 120, minWidth: 100 }}
            >
              <option value="">Selecciona código de área</option>
              {areaCodes.map(c => (
                <option key={c.code} value={c.phone_area_code}>
                  {c.code} - {c.phone_area_code}
                </option>
              ))}
            </Input>
            <Input
              id="owner_phone"
              type="text"
              placeholder="Teléfono"
              value={editableInfo.phone}
              disabled={false}
              onChange={e => setEditableInfo({ ...editableInfo, phone: e.target.value })}
              className="edit-modal-input"
              style={{ flex: 1, minWidth: 0 }}
            />
          </div>
        </FormGroup>
        <FormGroup>
          <Label for="owner_first_name">Primer nombre</Label>
          <Input
            id="owner_first_name"
            type="text"
            value={editableInfo.first_name ?? ''}
            disabled={hasNoOwner ? false : true}
            onChange={e => setEditableInfo({ ...editableInfo, first_name: e.target.value })}
            className="edit-modal-input"
          />
        </FormGroup>
        <FormGroup>
          <Label for="owner_last_name">Apellido</Label>
          <Input
            id="owner_last_name"
            type="text"
            value={editableInfo.last_name ?? ''}
            disabled={hasNoOwner ? false : true}
            onChange={e => setEditableInfo({ ...editableInfo, last_name: e.target.value })}
            className="edit-modal-input"
          />
        </FormGroup>
        {hasNoOwner && (
          <div style={{ color: '#d9534f', marginBottom: 8 }}>
            Esta propiedad no tiene propietario asignado. Búscalo y selecciónalo.
          </div>
        )}
      </ModalBody>
      <ModalFooter className="edit-modal-footer">
        <Button
          color="primary"
          className="edit-modal-save-btn"
          onClick={() => {
            setOwnerProperty({
              ...ownerProperty,
              landlordInfo: {
                ...ownerProperty.landlordInfo,
                ...editableInfo,
              },
            });
            onSave(editableInfo);
          }}
        >
          Guardar
        </Button>
        <Button color="secondary" onClick={toggle}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditOwnerModal;
